import { useState, useEffect } from 'react'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'

const WEEKLY_SUMMARY_KEY = 'ekkam_weekly_summary_dismissed'

interface WeekSummary {
  discussions: number
  comments: number
  aiResponses: number
  newFollowers: number
  activeDays: number
  totalContributions: number
}

function getISOWeek(d: Date): string {
  const date = new Date(d)
  date.setUTCHours(0, 0, 0, 0)
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return `${date.getUTCFullYear()}-W${weekNo}`
}

export default function WeeklySummaryBanner() {
  const { user, isAuthenticated, accessToken } = useAppSelector((s) => s.auth)
  const [summary, setSummary] = useState<WeekSummary | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !user || !accessToken) return
    const thisWeek = getISOWeek(new Date())
    const dismissed = localStorage.getItem(WEEKLY_SUMMARY_KEY)
    if (dismissed === thisWeek) return

    api.get<WeekSummary>('users', ENDPOINTS.users.weeklySummary(user.id), accessToken)
      .then((data) => {
        if (data.totalContributions > 0 || data.aiResponses > 0 || data.newFollowers > 0) {
          setSummary(data)
          setVisible(true)
        }
      })
      .catch(() => {})
  }, [isAuthenticated, user?.id, accessToken])

  const dismiss = () => {
    localStorage.setItem(WEEKLY_SUMMARY_KEY, getISOWeek(new Date()))
    setVisible(false)
  }

  if (!visible || !summary) return null

  const stats: { icon: string; value: number; label: string; color?: string }[] = [
    ...(summary.discussions > 0 ? [{ icon: '✍️', value: summary.discussions, label: `discussion${summary.discussions !== 1 ? 's' : ''}` }] : []),
    ...(summary.comments > 0 ? [{ icon: '💬', value: summary.comments, label: `comment${summary.comments !== 1 ? 's' : ''}` }] : []),
    ...(summary.aiResponses > 0 ? [{ icon: '🤖', value: summary.aiResponses, label: `AI answer${summary.aiResponses !== 1 ? 's' : ''}`, color: 'var(--accent-primary)' }] : []),
    ...(summary.newFollowers > 0 ? [{ icon: '👥', value: summary.newFollowers, label: `new follower${summary.newFollowers !== 1 ? 's' : ''}`, color: 'var(--success)' }] : []),
    ...(summary.activeDays > 0 ? [{ icon: '📅', value: summary.activeDays, label: `active day${summary.activeDays !== 1 ? 's' : ''}` }] : []),
  ]

  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem',
      zIndex: 9999, maxWidth: '360px', width: 'calc(100vw - 3rem)',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: '16px', padding: '1.25rem 1.5rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      animation: 'wsFadeIn 0.3s ease',
    }}>
      <style>{`@keyframes wsFadeIn { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }`}</style>

      <button
        onClick={dismiss}
        style={{ position: 'absolute', top: '0.875rem', right: '0.875rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
      >✕</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
        <span style={{ fontSize: '1.25rem' }}>📅</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Your week on Ekkam</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Last 7 days recap</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.35rem 0.65rem',
          }}>
            <span style={{ fontSize: '0.9rem' }}>{s.icon}</span>
            <span style={{ fontWeight: 700, fontSize: '0.875rem', color: s.color ?? 'var(--text-primary)' }}>{s.value}</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '0.875rem' }}>
        {summary.totalContributions >= 10
          ? '🔥 Amazing week! Keep the momentum going.'
          : summary.totalContributions >= 5
          ? "👏 Solid week! You're building great habits."
          : summary.activeDays >= 3
          ? '📈 Good consistency! Try to post a bit more this week.'
          : '💪 Every contribution counts. Keep showing up!'}
      </div>

      <button
        onClick={dismiss}
        style={{ width: '100%', padding: '0.5rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}
      >
        Thanks! 🚀
      </button>
    </div>
  )
}
